import { useOutletContext } from "react-router-dom";
import theme from "../../../../theme";
import { Loader } from "../../../../components/Loader";
import { CardLine } from "../../../IndividualView";
import { Flex } from "../../../../components/Flex";
import { Paragraph } from "../../../../components/Paragraph";
import { DateTime } from "luxon";
import { Box } from "../../../../components/Box";
import { useFillViewportHeight } from "../../../../hooks/useFillViewportHeight";
import { useProfileChecks } from "../../../../data/useProfileChecks";
import { Button } from "../../../../components/Button";
import { Tooltip } from "../../../../components/Tooltip";
import { Image } from "../../../../components/Image";
import { capitalise } from "../../../../utils/string";
import { CHECK_ICONS } from "../../../../utils/screeningChecks";
import success from "../../../../assets/card-success-icon.svg";
import inReview from "../../../../assets/card-in-review-icon.svg";
import pending from "../../../../assets/pending-icon.svg";
import { Profile } from "../../../../types/Profile";

export const ScreeningTab = ({
  profile: profileOverride,
}: {
  profile?: Profile;
}) => {
  const { ref: fillRef, height: fillHeight } = useFillViewportHeight();
  const outlet = useOutletContext<{ profile: Profile } | null>();
  const profile = profileOverride ?? outlet?.profile;
  const { data, isLoading, isError } = useProfileChecks(profile?.id);

  const checksIcons: Record<string, any> = CHECK_ICONS;

  if (isLoading) return <Loader />;
  if (isError) return <CardLine>Failed to load screening.</CardLine>;

  const checks = data?.results ?? [];

  const getCheckDisplayName = (item: any) => {
    if (item.company_name) return item.company_name;

    const parts = [item.first_name, item.middle_name, item.last_name].filter(
      Boolean,
    );
    if (parts.length) return parts.join(" ");

    return item.reference ?? "-";
  };

  const getCheckViewUrl = (item: any) =>
    `/screening/${item.mandate}/individuals/${item.id}`;

  return (
    <Box ref={fillRef} height={fillHeight} overflowY="auto" overflowX="hidden">
      {checks.length ? (
        <Box
          style={{
            background: theme.colors.white,
            borderRadius: 12,
            border: `1px solid ${theme.colors.divider}`,
            overflow: "hidden",
          }}
        >
          {/* Header row */}
          <Flex
            justifyContent="space-between"
            alignItems="center"
            style={{
              padding: 14,
              borderBottom: `1px solid ${theme.colors.divider}`,
              background: "#F9FAFB",
            }}
          >
            <Paragraph my={0} fontWeight={600} width="22%" color="gray.60">
              Name
            </Paragraph>

            <Paragraph my={0} fontWeight={600} width="18%" color="gray.60">
              Reference
            </Paragraph>

            <Paragraph my={0} fontWeight={600} width="22%" color="gray.60">
              Checks
            </Paragraph>

            <Paragraph my={0} fontWeight={600} width="22%" color="gray.60">
              Result
            </Paragraph>

            <Paragraph my={0} fontWeight={600} width="10%" color="gray.60">
              Created
            </Paragraph>

            <Box width="6%" />
          </Flex>

          {checks.map((item: any) => {
            const createdOn = item.created_on
              ? DateTime.fromISO(item.created_on).toFormat("dd/MM/yyyy")
              : "-";

            const itemChecks: any[] = Array.isArray(item.checks) ? item.checks : [];

            const checks_completed = item.checks_completed ?? 0;
            const checks_in_review = item.checks_in_review ?? 0;
            const checks_in_pending = item.checks_in_pending ?? 0;

            return (
              <Flex
                key={item.id}
                justifyContent="space-between"
                alignItems="center"
                style={{
                  padding: 14,
                  borderBottom: `1px solid ${theme.colors.divider}`,
                }}
              >
                <Paragraph fontWeight={600} my={0} width="22%">
                  {getCheckDisplayName(item)}
                </Paragraph>

                <Paragraph my={0} width="18%" color="gray.60">
                  {item.reference ?? "-"}
                </Paragraph>

                {/* Checks column */}
                <Box width="22%">
                  <Flex justifyContent="start" alignItems="center">
                    {itemChecks.length ? (
                      itemChecks.map((check: any) => (
                        <span key={check.id ?? check.name} style={{ margin: "0 5px" }}>
                          <Tooltip content={capitalise(String(check.name).replace(/_/g, " "))}>
                            <Image
                              src={checksIcons[check.name]}
                              alt="checks"
                              height="auto"
                              width="22px"
                              mr={1}
                            />
                          </Tooltip>
                        </span>
                      ))
                    ) : (
                      <Paragraph my={0} color="gray.60">
                        -
                      </Paragraph>
                    )}
                  </Flex>
                </Box>

                {/* Result column */}
                <Box width="22%">
                  <Flex justifyContent="start" alignItems="center">
                    <Flex gap="6" mr={2} alignItems="center">
                      <Tooltip content="Screening completed">
                        <Image src={success} alt="success" height="20px" width="24px" />
                      </Tooltip>
                      {checks_completed}
                    </Flex>

                    <Flex gap="6" mr={2} alignItems="center">
                      <Tooltip content="Screening in review">
                        <Image src={inReview} alt="inReview" height="20px" width="24px" />
                      </Tooltip>
                      {checks_in_review}
                    </Flex>

                    <Flex gap="6" mr={2} alignItems="center">
                      <Tooltip content="Screening pending">
                        <Image src={pending} alt="pending" height="20px" width="24px" />
                      </Tooltip>
                      {checks_in_pending}
                    </Flex>
                  </Flex>
                </Box>

                <Paragraph color="gray.60" my={0} width="10%">
                  {createdOn}
                </Paragraph>

                <Box width="6%" style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="primary"
                    onClick={() => window.open(getCheckViewUrl(item), "_blank")}
                  >
                    View
                  </Button>
                </Box>
              </Flex>
            );
          })}
        </Box>
      ) : (
        <CardLine>No screening found.</CardLine>
      )}
    </Box>
  );
};
